import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import GenericModal from "@ui-components/Modal/GenericModal";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import StatusBadge from "@ui-components/Tag/StatusBadge";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { parseAxiosError } from "@shared/components/utils/apiErrorParser";
import {
	createSubscription,
	listAvailableServices,
} from "./requests/subscriptionsApi";
import {
	getStateBadge,
	parseSubscriptionDate,
	SERVICE_TYPE_LABEL,
} from "./SubscriptionsData";
import type {
	AvailableService,
	CreatedSubscription,
	SubscriptionServiceType,
} from "./types";

/** Terms offered as one-click choices; anything else goes in the custom field. */
const QUICK_TERMS = [6, 12, 24, 36] as const;

const CURRENCIES = ["USD", "MXN", "EUR"] as const;

const SERVICE_TYPES = Object.keys(
	SERVICE_TYPE_LABEL
) as SubscriptionServiceType[];

/**
 * The term a manual creation will produce. Inclusive range, same rule as a
 * renewal: N months from the start, minus a day.
 *
 * Returns null whenever the range cannot be built, which the caller renders as
 * "no preview" rather than trying to format it. Two ways that happens, and the
 * second is why this is a function of its own (CTX-529):
 *
 *   - the start date cannot be parsed at all;
 *   - the start date parses but the term arithmetic overflows. `<input
 *     type="date">` accepts a year of any length, and a start close to the
 *     largest date JavaScript can hold (year 275760) pushes `end` past it. The
 *     result is an Invalid Date — an OBJECT, so truthy — which sailed past the
 *     old `if (!start)` guard and reached `format()`. That throws, and with no
 *     error boundary on this branch it unmounted the whole app rather than just
 *     this modal: a typed year blanked the page.
 */
export function computeCreateTerm({
	contractStart,
	termMonths,
	now,
}: {
	contractStart: string;
	termMonths: number;
	now: Date;
}): { start: Date; end: Date; startsLater: boolean } | null {
	if (!Number.isInteger(termMonths) || termMonths <= 0) return null;

	const start = contractStart ? parseSubscriptionDate(contractStart) : now;
	if (!start || Number.isNaN(start.getTime())) return null;

	const end = new Date(start);
	end.setMonth(end.getMonth() + termMonths);
	end.setDate(end.getDate() - 1);
	if (Number.isNaN(end.getTime())) return null;

	return { start, end, startsLater: start > now };
}

interface CreateSubscriptionModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** Called after a successful creation so the table can refetch. */
	onCreated: (created: CreatedSubscription) => void;
	/** Injected for deterministic tests; defaults to the real clock. */
	now?: Date;
}

/**
 * Manually create a subscription for an existing service (CTX-208).
 *
 * This is the escape hatch, not the normal path — subscriptions come from the
 * ordering flow. It exists so a Global Admin can cover a service the automated
 * path left without one, and the copy says so, because reaching for it when
 * ordering should have handled it hides a real problem.
 *
 * The service is chosen from the services that have no coverage today
 * (`GET .../available-services`), so a conflict is impossible to pick rather
 * than reported after the fact. The backend re-checks anyway — the list can go
 * stale between opening the form and submitting it.
 */
const CreateSubscriptionModal: React.FC<CreateSubscriptionModalProps> = ({
	isOpen,
	onClose,
	onCreated,
	now,
}) => {
	const api = useAxiosAuth0();
	const [serviceType, setServiceType] = useState<SubscriptionServiceType>("port");
	const [selected, setSelected] = useState<AvailableService | null>(null);
	const [serviceSearch, setServiceSearch] = useState<string>("");
	const [options, setOptions] = useState<AvailableService[]>([]);
	const [loadingOptions, setLoadingOptions] = useState(false);
	const [hasMoreOptions, setHasMoreOptions] = useState(false);
	const [termMonths, setTermMonths] = useState<number>(12);
	const [contractStart, setContractStart] = useState<string>("");
	const [autoRenew, setAutoRenew] = useState<boolean>(false);
	const [purchaseOrder, setPurchaseOrder] = useState<string>("");
	const [currencyCode, setCurrencyCode] = useState<string>("");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const evaluatedAt = useMemo(() => now ?? new Date(), [now]);

	const termIsValid = Number.isInteger(termMonths) && termMonths > 0;

	// Only services with no current coverage are offered, so a conflict is
	// impossible to pick rather than reported after the fact. Debounced because
	// the search hits 13 service tables on the backend.
	useEffect(() => {
		if (!isOpen) return undefined;
		let active = true;
		const timer = setTimeout(() => {
			setLoadingOptions(true);
			listAvailableServices(api, {
				serviceType,
				search: serviceSearch.trim() || undefined,
				limit: 25,
			})
				.then((response) => {
					if (!active) return;
					setOptions(response.items);
					setHasMoreOptions(response.hasMore);
				})
				.catch((err) => {
					if (active) setError(parseAxiosError(err).message);
				})
				.finally(() => {
					if (active) setLoadingOptions(false);
				});
		}, 250);
		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [api, isOpen, serviceType, serviceSearch]);

	/**
	 * Preview of the term the backend will produce. Inclusive range, same rule
	 * as a renewal: N months from the start, minus a day.
	 */
	const preview = useMemo(
		() => computeCreateTerm({ contractStart, termMonths, now: evaluatedAt }),
		[contractStart, termMonths, evaluatedAt]
	);

	/** A date was typed, the term is fine, and the range still cannot be built. */
	const startDateUnusable =
		contractStart !== "" && termIsValid && preview === null;

	// Defined after the preview on purpose: a term the app cannot even compute
	// is not one it should offer to create.
	const canSubmit =
		selected !== null && termIsValid && preview !== null && !submitting;

	const resetForm = () => {
		setServiceType("port");
		setSelected(null);
		setServiceSearch("");
		setOptions([]);
		setTermMonths(12);
		setContractStart("");
		setAutoRenew(false);
		setPurchaseOrder("");
		setCurrencyCode("");
		setError(null);
	};

	const handleClose = () => {
		resetForm();
		onClose();
	};

	const handleCreate = async () => {
		setSubmitting(true);
		setError(null);
		try {
			if (!selected) return;
			const created = await createSubscription(api, {
				serviceType: selected.serviceType,
				serviceId: selected.serviceId,
				termMonths,
				contractStart: contractStart || null,
				autoRenew,
				purchaseOrder: purchaseOrder.trim() || null,
				currencyCode: currencyCode || null,
			});
			onCreated(created);
			resetForm();
			onClose();
		} catch (err) {
			setError(parseAxiosError(err).message);
		} finally {
			setSubmitting(false);
		}
	};

	if (!isOpen) return null;

	const newStateBadge = getStateBadge(preview?.startsLater ? "future" : "active");

	return (
		<GenericModal
			isOpen
			onClose={handleClose}
			size="lg"
			title={
				<div className="flex flex-col min-w-0">
					<span className="text-h5 font-bold">New subscription</span>
					<span className="text-sm font-normal text-semantic-text-secondary mt-0.5">
						For a service the ordering flow left without one
					</span>
				</div>
			}
			footer={
				<div className="flex items-center gap-2 w-full">
					<div className="flex-1" />
					<GenericButton
						label="Cancel"
						variant="secondary"
						size="md"
						onClick={handleClose}
						disabled={submitting}
					/>
					<GenericButton
						label="Create subscription"
						variant="brand"
						size="md"
						loading={submitting}
						disabled={!canSubmit}
						onClick={() => {
							void handleCreate();
						}}
					/>
				</div>
			}
		>
			<div className="flex flex-col gap-5">
				<p className="rounded-sm border border-semantic-border-primary bg-semantic-bg-secondary px-4 py-3 text-sm text-semantic-text-secondary">
					Subscriptions are normally created by the ordering flow. Use this when
					a service ended up without one — the customer is taken from the
					service itself, and a service that is already covered will be
					rejected.
				</p>

				<fieldset className="rounded-sm border border-semantic-border-primary p-4 flex flex-col gap-4">
					<legend className="px-1 text-sm font-bold">Service</legend>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
						<div className="flex flex-col space-y-2 min-w-0">
							<label
								htmlFor="create-service-type"
								className="text-sm font-medium text-semantic-text-primary"
							>
								Service type
							</label>
							<select
								id="create-service-type"
								value={serviceType}
								onChange={(event) =>
									setServiceType(event.target.value as SubscriptionServiceType)
								}
								className="w-full py-2 px-3 text-sm rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt text-semantic-text-primary"
							>
								{SERVICE_TYPES.map((type) => (
									<option key={type} value={type}>
										{SERVICE_TYPE_LABEL[type]}
									</option>
								))}
							</select>
						</div>
						<div className="min-w-0">
							<GenericInput
								label="Find a service"
								placeholder="Search by name"
								value={serviceSearch}
								onChange={(value) => {
									setServiceSearch(value);
									setSelected(null);
								}}
							/>
						</div>
					</div>

					<div className="flex flex-col gap-2">
						<span className="text-sm font-medium text-semantic-text-primary">
							Available services
							<span className="ml-2 font-normal text-semantic-text-secondary">
								only services nothing covers today
							</span>
						</span>
						<ul className="list-none p-1 m-0 max-h-52 overflow-y-auto rounded-sm border border-semantic-border-primary">
							{loadingOptions && (
								<li className="px-3 py-3 text-sm text-semantic-text-secondary">
									Searching…
								</li>
							)}
							{!loadingOptions && options.length === 0 && (
								<li className="px-3 py-3 text-sm text-semantic-text-secondary">
									No uncovered {SERVICE_TYPE_LABEL[serviceType]} services here. Every
									one of them already has a subscription.
								</li>
							)}
							{!loadingOptions &&
								options.map((service) => {
									const isSelected =
										selected?.serviceType === service.serviceType &&
										selected?.serviceId === service.serviceId;
									return (
										<li key={`${service.serviceType}:${service.serviceId}`}>
											<button
												type="button"
												onClick={() => setSelected(service)}
												aria-pressed={isSelected}
												className={`w-full text-left flex items-baseline gap-2 px-3 py-2 rounded-xsm ${
													isSelected
														? "bg-semantic-bg-brand-secondary text-semantic-text-brand"
														: "hover:bg-semantic-state-active"
												}`}
											>
												<span className="font-medium truncate">
													{service.serviceName}
												</span>
												<span className="text-sm text-semantic-text-secondary truncate">
													{service.customerName ?? "—"}
												</span>
												<span className="ml-auto text-xs text-semantic-text-secondary tabular-nums">
													#{service.serviceId}
												</span>
											</button>
										</li>
									);
								})}
						</ul>
						{hasMoreOptions && (
							<span className="text-sm text-semantic-text-secondary">
								More services match — narrow the search to see them.
							</span>
						)}
					</div>
				</fieldset>

				<fieldset className="rounded-sm border border-semantic-border-primary p-4 flex flex-col gap-3">
					<legend className="px-1 text-sm font-bold">Term</legend>
					<div className="flex flex-wrap gap-2">
						{QUICK_TERMS.map((months) => {
							const selected = termMonths === months;
							return (
								<button
									key={months}
									type="button"
									onClick={() => setTermMonths(months)}
									aria-pressed={selected}
									className={`px-3.5 py-1 rounded-round border text-sm transition-colors ${
										selected
											? "border-semantic-border-brand-secondary bg-semantic-bg-brand-secondary text-semantic-text-brand font-bold"
											: "border-semantic-border-secondary text-semantic-text-primary hover:bg-semantic-state-active"
									}`}
								>
									{months} months
								</button>
							);
						})}
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
						<div className="min-w-0">
							<GenericInput
								label="Or a custom term"
								type="number"
								value={termMonths}
								unit="months"
								hasError={!termIsValid}
								helperText={
									termIsValid
										? undefined
										: "Enter a whole number of months, 1 or more."
								}
								onChange={(value) => setTermMonths(Number(value))}
							/>
						</div>
						<div className="flex flex-col space-y-2 min-w-0">
							<label
								htmlFor="create-start"
								className="text-sm font-medium text-semantic-text-primary"
							>
								Start date
							</label>
							<input
								id="create-start"
								type="date"
								value={contractStart}
								onChange={(event) => setContractStart(event.target.value)}
								className="w-full py-2 px-3 text-sm rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt text-semantic-text-primary"
							/>
							<span
								className={`text-sm ${
									startDateUnusable
										? "text-semantic-text-danger"
										: "text-semantic-text-secondary"
								}`}
							>
								{startDateUnusable
									? "That start date is out of range. Enter a four-digit year."
									: "Leave empty to start today."}
							</span>
						</div>
					</div>

					{preview && (
						<div
							className="rounded-sm border border-semantic-border-primary bg-semantic-bg-brand-secondary px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2"
							aria-live="polite"
						>
							<span className="text-sm text-semantic-text-secondary">
								Resulting term
							</span>
							<span className="text-sm font-bold tabular-nums">
								{format(preview.start, "dd MMM yyyy")} →{" "}
								{format(preview.end, "dd MMM yyyy")}
							</span>
							<StatusBadge
								variant={newStateBadge.variant}
								label={newStateBadge.label}
							/>
						</div>
					)}
				</fieldset>

				<fieldset className="rounded-sm border border-semantic-border-primary p-4 flex flex-col gap-4">
					<legend className="px-1 text-sm font-bold">Options</legend>
					<div className="flex items-center gap-3">
						<div className="flex-1 min-w-0">
							<span className="block text-sm font-semibold">
								Auto-renew at term end
							</span>
							<span className="text-sm text-semantic-text-secondary">
								Applies to this subscription.
							</span>
						</div>
						<GenericSwitch checked={autoRenew} onChange={setAutoRenew} />
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
						<div className="min-w-0">
							<GenericInput
								label="Purchase order"
								placeholder="PO-2026-0148"
								value={purchaseOrder}
								onChange={setPurchaseOrder}
							/>
						</div>
						<div className="flex flex-col space-y-2 min-w-0">
							<label
								htmlFor="create-currency"
								className="text-sm font-medium text-semantic-text-primary"
							>
								Currency
							</label>
							<select
								id="create-currency"
								value={currencyCode}
								onChange={(event) => setCurrencyCode(event.target.value)}
								className="w-full py-2 px-3 text-sm rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt text-semantic-text-primary"
							>
								<option value="">Not set</option>
								{CURRENCIES.map((code) => (
									<option key={code} value={code}>
										{code}
									</option>
								))}
							</select>
						</div>
					</div>
				</fieldset>

				{error && (
					<p className="rounded-sm border border-semantic-border-danger-primary bg-semantic-bg-danger-secondary px-4 py-3 text-sm text-semantic-text-danger">
						{error}
					</p>
				)}
			</div>
		</GenericModal>
	);
};

export default CreateSubscriptionModal;
